<script lang="ts" setup>
	import { ref, watch } from 'vue'

	import SlideOver from '@/components/SlideOver.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledDateInput from '@/components/StyledDateInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import { useAuth } from '@/composables/useAuth'

	import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
	import { Company } from '@/modules/companies/interfaces/company'
	import { useSearchHotels } from '@/modules/hotels/composables/useSearchHotels'
	import { HotelAmadues } from '@/modules/hotels/interfaces/hotel'
	import useUsersSearch from '@/modules/users/composables/useUsersSearch'
	import { User } from '@/modules/users/interfaces/user'

	import { Filters } from '../pages/HotelReportsPage.vue'

	interface Props {
		showFilters: boolean
		selectedCompany: Company | null
		filtres: Filters
	}

	interface Emits {
		(e: 'update:showFilters', value: boolean): void
		(e: 'update:selectedCompany', value: Company | null): void
		(e: 'update:filtres', filtres: Props['filtres']): void
	}

	const props = defineProps<Props>()

	const emit = defineEmits<Emits>()

	const { user } = useAuth()

	const localSelectedCompany = ref<Company | null>(props.selectedCompany)

	const selectedUser = ref<User | null>(null)
	const selectedHotel = ref<HotelAmadues | null>(null)

	watch(
		() => props.selectedCompany,
		(newValue) => {
			localSelectedCompany.value = newValue
		}
	)

	const {
		companies,
		setQuery,
		isLoading: loadingCompanies,
	} = useCompaniesSearch({
		immediate: true,
		params: {
			corporate: user.value?.corporationId,
		},
	})

	const {
		data: users,
		isLoading: loadingUsers,
		isFetching: isFetchingUsers,
		setQuery: setUsersQuery,
		refetch: refetchUsers,
	} = useUsersSearch({
		automaticSearch: false,
		immediate: false,
		params: {
			filters: {
				company:
					localSelectedCompany.value ?
						localSelectedCompany.value.id
					:	undefined,
			},
		},
	})

	const {
		hotels,
		isFetching: isFetchingHotels,
		refetch: refetchHotels,
		searchQueryHotels,
	} = useSearchHotels({})

	const optionsFilterMoths = [
		{ label: 'Último mes', value: '1month' },
		{ label: 'Últimos 3 meses', value: '3months' },
		{ label: 'Últimos 6 meses', value: '6months' },
		{ label: 'Ultimo año', value: 'lastYear' },
		{ label: 'Proximo mes', value: '1monthNext' },
		{ label: 'Próximos 3 meses', value: '3monthNext' },
		{ label: 'Próximos 6 meses', value: '6monthNext' },
		{ label: 'Este año', value: 'year' },
		{ label: 'Personalizado', value: 'custom' },
	]

	const datePresentChange = (preset: string) => {
		const today = new Date()
		let startDate: Date = today
		let endDate: Date = today

		// Casos especiales
		if (preset === 'custom') return
		if (preset === 'year') {
			const year = today.getFullYear()

			startDate = new Date(year, 0, 1)
			endDate = new Date(year, 11, 31)

			return { startDate, endDate }
		}
		if (preset === 'lastYear') {
			const lastYear = today.getFullYear() - 1

			startDate = new Date(lastYear, 0, 1)
			endDate = new Date(lastYear, 11, 31)
			return { startDate, endDate }
		}

		const monthsMatch = preset.match(/(\d+)month/)
		if (!monthsMatch) return

		const months = parseInt(monthsMatch[1])
		const isNext = preset.includes('Next')

		if (isNext) {
			startDate = new Date(today.getFullYear(), today.getMonth(), 1)
			endDate = new Date(today.getFullYear(), today.getMonth() + months, 0)
		} else {
			startDate = new Date(today.getFullYear(), today.getMonth() - months, 1)
			endDate = new Date(today.getFullYear(), today.getMonth(), 0)
		}

		return { startDate, endDate }
	}

	const applyDatePreset = (preset: string) => {
		const dates = datePresentChange(preset)

		if (!dates) return 'custom'

		const newDateRange = [
			dates?.startDate.toISOString().split('T')[0],
			dates?.endDate.toISOString().split('T')[0],
		]

		emit('update:filtres', { ...props.filtres, dateRange: newDateRange })
	}

	const applyCreationDatePreset = (preset: string) => {
		const dates = datePresentChange(preset)

		if (!dates) return 'custom'

		const newCreationDateRange = [
			dates.startDate.toISOString().split('T')[0],
			dates.endDate.toISOString().split('T')[0],
		]
		emit('update:filtres', {
			...props.filtres,
			creationDateRange: newCreationDateRange,
		})
	}

	watch(
		() => props.filtres.datePreset,
		(newPreset) => {
			applyDatePreset(newPreset)
		},
		{
			immediate: true,
		}
	)

	watch(
		() => props.filtres.creationDatePreset,
		(newPreset) => {
			if (!newPreset || newPreset === '') {
				emit('update:filtres', {
					...props.filtres,
					creationDateRange: undefined,
				})
				return
			}
			applyCreationDatePreset(newPreset)
		}
	)
</script>

<template>
	<SlideOver
		:when="showFilters"
		title="Filtros de Reportes"
		:hide-buttons="true"
		@update:close-slide-over="() => emit('update:showFilters', false)"
	>
		<div class="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-full">
			<!-- Header Section -->
			<div class="mb-8 relative">
				<div
					class="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl opacity-30 transform -skew-y-1"
				></div>

				<div
					class="relative bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
				>
					<div class="flex items-center gap-4 mb-3">
						<div class="p-3 bg-blue-100 rounded-lg">
							<svg
								class="w-6 h-6 text-blue-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
								></path>
							</svg>
						</div>
						<div>
							<h3 class="text-xl font-bold text-gray-900">Filtros Avanzados</h3>
							<p class="text-sm text-gray-600 mt-1">
								Personaliza tus reportes de hotelería
							</p>
						</div>
					</div>
				</div>
			</div>

			<div class="space-y-8">
				<!-- Sección: Empresa -->
				<div class="relative">
					<div
						class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
					>
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-purple-100 rounded-lg">
								<svg
									class="w-5 h-5 text-purple-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
									></path>
								</svg>
							</div>
							<h4 class="font-semibold text-gray-900">Selección de Empresa</h4>
						</div>

						<StyledCombobox
							:modelValue="localSelectedCompany"
							@update:modelValue="
								(value: Company | null) => {
									localSelectedCompany = value
									emit('update:selectedCompany', value)
								}
							"
							label="Empresa"
							placeholder="Seleccionar empresa"
							:options="companies || []"
							:loading-options="loadingCompanies"
							:on-input-change="localSelectedCompany ? undefined : setQuery"
							:show-input="!localSelectedCompany"
							:prevent-auto-focus="true"
							:prevent-scroll-on-focus="false"
							class="mb-2"
						>
							<template #default="{ option }">
								<span>{{ option.name }}</span>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<p class="text-sm normal-case md:text-base truncate">
										{{ localSelectedCompany?.name }}
									</p>

									<button
										class="material-symbols-outlined text-[22px] hover:text-gray-700 transition-colors"
										@click.prevent="
											() => {
												localSelectedCompany = null
												emit('update:selectedCompany', null)
											}
										"
									>
										close
									</button>
								</div>
							</template>
						</StyledCombobox>
					</div>
				</div>

				<!-- Sección: Fechas -->
				<div
					class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
				>
					<div
						class="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-gray-200"
					>
						<div class="flex items-center gap-3">
							<div class="p-2 bg-orange-100 rounded-lg">
								<svg
									class="w-5 h-5 text-orange-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
									></path>
								</svg>
							</div>
							<h4 class="font-semibold text-gray-900">Filtros de Fecha</h4>
							<span
								class="text-xs text-gray-500 bg-orange-100 px-2 py-1 rounded-full"
							>
								Selecciona rangos de fechas
							</span>
						</div>
					</div>

					<div class="p-6 space-y-6">
						<!-- Selector de Preset de Fechas -->
						<div class="space-y-2">
							<div class="flex items-center gap-2 mb-2">
								<svg
									class="w-4 h-4 text-orange-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
									></path>
								</svg>
								<span class="text-sm font-medium text-gray-700">
									Período de tiempo
								</span>
							</div>
							<StyledSelect
								:modelValue="filtres.datePreset"
								label=""
								:options="optionsFilterMoths"
								@update:modelValue="
									(value: string) =>
										emit('update:filtres', { ...filtres, datePreset: value })
								"
							/>
						</div>

						<!-- Rango de fechas de viaje (visible solo si es personalizado) -->
						<div
							v-if="filtres.datePreset === 'custom'"
							class="space-y-2"
						>
							<div class="flex items-center gap-2 mb-2">
								<svg
									class="w-4 h-4 text-orange-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									></path>
								</svg>
								<span class="text-sm font-medium text-gray-700">
									Fechas de reservas
								</span>
							</div>
							<StyledDateInput
								:modelValue="filtres.dateRange"
								label=""
								placeholder="Seleccionar rango de fechas"
								:range="true"
								:auto-apply="true"
								@update:modelValue="
									(value: any) =>
										emit('update:filtres', { ...filtres, dateRange: value })
								"
							/>
							<p class="text-xs text-gray-500">
								Filtra por fechas de check-in o check-out
							</p>
						</div>

						<!-- Selector de Preset de Fechas de Creación -->
						<div class="space-y-2">
							<div class="flex items-center gap-2 mb-2">
								<svg
									class="w-4 h-4 text-orange-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 6v6m0 0v6m0-6h6m-6 0H6"
									></path>
								</svg>
								<span class="text-sm font-medium text-gray-700">
									Período de creación
								</span>
							</div>
							<StyledSelect
								:modelValue="filtres.creationDatePreset"
								label=""
								:options="
									[{ label: 'Seleccione una opción', value: '' }]
										.concat(optionsFilterMoths)
										.filter((i) => !i.value.includes('monthNext'))
										.filter((i) => !i.value.includes('year'))
								"
								placeholder="Seleccione una opción"
								@update:modelValue="
									(value: string) =>
										emit('update:filtres', {
											...filtres,
											creationDatePreset: value,
										})
								"
							/>
							<p class="text-xs text-gray-500">
								Filtra por cuándo se crearon las reservas (opcional)
							</p>
						</div>

						<!-- Rango de fechas de creación (visible solo si es personalizado) -->
						<div
							v-if="filtres.creationDatePreset === 'custom'"
							class="space-y-2"
						>
							<div class="flex items-center gap-2 mb-2">
								<svg
									class="w-4 h-4 text-orange-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 6v6m0 0v6m0-6h6m-6 0H6"
									></path>
								</svg>
								<span class="text-sm font-medium text-gray-700">
									Fechas de creación
								</span>
							</div>
							<StyledDateInput
								:modelValue="filtres.creationDateRange"
								label=""
								placeholder="Seleccionar rango de fechas de creación"
								:range="true"
								:auto-apply="true"
								@update:modelValue="
									(value: any) =>
										emit('update:filtres', {
											...filtres,
											creationDateRange: value,
										})
								"
							/>
							<p class="text-xs text-gray-500">
								Filtra por cuándo se crearon las reservas
							</p>
						</div>
					</div>
				</div>

				<!-- Sección: Búsqueda por viajero -->
				<div class="relative">
					<div
						class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
					>
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-green-100 rounded-lg">
								<svg
									class="w-5 h-5 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									></path>
								</svg>
							</div>
							<h4 class="font-semibold text-gray-900">Búsqueda por Viajero</h4>
						</div>

						<StyledCombobox
							v-model="selectedUser"
							@update:modelValue="
								(value: User | null) =>
									emit('update:filtres', {
										...filtres,
										searchTravelerId: value?.id || null,
										searchTravelerName: value?.fullName || null,
									})
							"
							label="Nombre del viajero"
							placeholder="Buscar por nombre del viajero"
							:options="users?.map((user) => user) || []"
							:loading-options="loadingUsers || isFetchingUsers"
							:on-input-change="
								(e) => {
									setUsersQuery(e)
									refetchUsers()
								}
							"
							:show-input="!selectedUser"
							:prevent-auto-focus="true"
							:prevent-scroll-on-focus="false"
							class="mb-2"
						>
							<template #default="{ option }">
								<span>{{ option.fullName }}</span>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<p class="text-sm normal-case md:text-base truncate">
										{{ selectedUser?.fullName }}
									</p>

									<button
										class="material-symbols-outlined text-[22px] hover:text-gray-700 transition-colors"
										@click.prevent="
											() => {
												emit('update:filtres', {
													...filtres,
													searchTravelerId: null,
													searchTravelerName: null,
												})
												selectedUser = null
											}
										"
									>
										close
									</button>
								</div>
							</template>
						</StyledCombobox>
					</div>
				</div>

				<!-- Sección: Búsqueda por hotel -->
				<div class="relative">
					<div
						class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
					>
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-blue-100 rounded-lg">
								<svg
									class="w-5 h-5 text-blue-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
									></path>
								</svg>
							</div>
							<h4 class="font-semibold text-gray-900">Búsqueda por Hotel</h4>
						</div>

						<StyledCombobox
							v-model="selectedHotel"
							@update:modelValue="
								(value: HotelAmadues | null) =>
									emit('update:filtres', {
										...filtres,
										searchHotelId: value?.id || null,
										searchHotelName: value?.name || null,
									})
							"
							label="Nombre del hotel"
							placeholder="Buscar por nombre del hotel"
							:options="hotels?.data || []"
							:loading-options="isFetchingHotels"
							:on-input-change="
								(e) => {
									searchQueryHotels = e
									refetchHotels()
								}
							"
							:show-input="!selectedHotel"
							:prevent-auto-focus="true"
							:prevent-scroll-on-focus="false"
							class="mb-2"
						>
							<template #default="{ option }">
								<span>{{ option.name }}</span>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<p class="text-sm normal-case md:text-base truncate">
										{{ selectedHotel?.name }}
									</p>

									<button
										class="material-symbols-outlined text-[22px] hover:text-gray-700 transition-colors"
										@click.prevent="
											() => {
												emit('update:filtres', {
													...filtres,
													searchHotelId: null,
													searchHotelName: null,
												})
												selectedHotel = null
											}
										"
									>
										close
									</button>
								</div>
							</template>
						</StyledCombobox>
					</div>
				</div>

				<!-- Sección: Ordenamiento -->
				<div
					class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
				>
					<div
						class="bg-gradient-to-r from-green-50 to-teal-50 px-6 py-4 border-b border-gray-200"
					>
						<div class="flex items-center gap-3">
							<div class="p-2 bg-green-100 rounded-lg">
								<svg
									class="w-5 h-5 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
									></path>
								</svg>
							</div>
							<h4 class="font-semibold text-gray-900">Ordenamiento</h4>
							<span
								class="text-xs text-gray-500 bg-green-100 px-2 py-1 rounded-full"
							>
								Ordena los resultados
							</span>
						</div>
					</div>

					<div class="p-6 space-y-6">
						<!-- Ordenar por -->
						<div class="space-y-2">
							<div class="flex items-center gap-2 mb-2">
								<svg
									class="w-4 h-4 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
									></path>
								</svg>
								<span class="text-sm font-medium text-gray-700">
									Ordenar por
								</span>
							</div>
							<StyledSelect
								:modelValue="filtres.orderBy"
								label=""
								:options="[
									{ label: 'Fecha de creación', value: 'created_at' },
									{ label: 'Fecha de inicio', value: 'start_date' },
									{ label: 'Fecha de fin', value: 'end_date' },
									{ label: 'Total', value: 'total' },
									{ label: 'Nombre del viajero', value: 'traveler_name' },
									{ label: 'Nombre del hotel', value: 'suppliable_name' },
								]"
								@update:modelValue="
									(value: string) =>
										emit('update:filtres', { ...filtres, orderBy: value })
								"
							/>
							<p class="text-xs text-gray-500">
								Selecciona el campo por el cual ordenar
							</p>
						</div>

						<!-- Dirección -->
						<div class="space-y-2">
							<div class="flex items-center gap-2 mb-2">
								<svg
									class="w-4 h-4 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M7 11l5-5m0 0l5 5m-5-5v12"
									></path>
								</svg>
								<span class="text-sm font-medium text-gray-700">
									Dirección
								</span>
							</div>
							<StyledSelect
								:modelValue="filtres.orderDirection"
								label=""
								:options="[
									{ label: 'Ascendente', value: 'asc' },
									{ label: 'Descendente', value: 'desc' },
								]"
								@update:modelValue="
									(value: 'asc' | 'desc') =>
										emit('update:filtres', {
											...filtres,
											orderDirection: value,
										})
								"
							/>
							<p class="text-xs text-gray-500">
								Selecciona la dirección del ordenamiento
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer con botón de limpiar -->
		<template #footer>
			<div
				class="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200"
			>
				<div class="flex items-center justify-center gap-4 p-4 w-full">
					<button
						@click.prevent="
							() => {
								// selectedCompany no se resetea aquí
								emit('update:filtres', {
									...filtres,
									statusFilter: 'all',
									datePreset: '3months',
									creationDatePreset: '',
									orderBy: 'created_at',
									orderDirection: 'desc',
								})
								selectedUser = null
								selectedHotel = null
							}
						"
						class="group inline-flex items-center justify-center px-5 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:scale-105 active:scale-95"
					>
						<svg
							class="w-4 h-4 mr-2 group-hover:rotate-180 transition-transform duration-300"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							></path>
						</svg>
						<span>Limpiar filtros</span>
					</button>
				</div>
			</div>
		</template>
	</SlideOver>
</template>
